import type { A11y } from '@snowit/icetypes';
import type { ButtonClick } from '@snowit/icetypes/events';

import testId from '@snowit/utils/testId';

import ButtonIcon from '../../Buttons/ButtonIcon';

import cx from 'classnames';
import s from './Spinner.module.scss';

export interface ISpinner {
  value: number;
  maxValue?: number;
  minValue?: number;
  name?: string;
  disabled?: boolean;
  onChange: (value: number, params: ButtonClick) => void;
  id?: string;
  className?: string;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
}

const Spinner = (props: ISpinner) => {
  const {
    value,
    maxValue = Number.POSITIVE_INFINITY,
    minValue = 0,
    name,
    disabled,
    onChange,
    className,
    a11y,
    test,
  } = props;

  const realValue = Math.max(Math.min(maxValue, value || minValue), minValue);

  const handleChange = (args: ButtonClick) => {
    const changedValue = value + (args.name === 'plus' ? 1 : -1);
    onChange(changedValue, args);
  };

  return (
    <div
      className={cx(s.wrapper, className)}
      role={'spinbutton'}
      aria-labelledby={a11y?.label}
      aria-valuemax={maxValue}
      aria-valuemin={minValue}
      aria-valuenow={realValue || minValue}
      aria-describedby={a11y?.describedBy}
    >
      <ButtonIcon
        icon={'minus'}
        iconColor={'text'}
        name={'minus'}
        disabled={disabled || realValue <= minValue}
        onClick={handleChange}
        theme={'ghost'}
        size={'db'}
        color={'text'}
        test={`${test}_minus`}
        a11y={{ label: 'Remove quantity' }}
      />
      <input
        className={s.field}
        max={maxValue}
        min={minValue}
        value={realValue || minValue}
        type='number'
        name={name}
        // disabled={true} // Removed to fix on iOS: it generated an opacity of 0.4
        readOnly
        tabIndex={-1}
        {...testId(`${test}_number`)}
      />
      <ButtonIcon
        icon={'plus'}
        name={'plus'}
        disabled={disabled || realValue >= maxValue}
        onClick={handleChange}
        theme={'ghost'}
        size={'db'}
        color={'text'}
        a11y={{ label: 'Add quantity' }}
        test={`${test}_plus`}
      />
    </div>
  );
};

export default Spinner;
